import React from "react";
import { FF, T } from "../lib/constants";

// Gedeelde bouwstenen van de nieuwe stijl (build 2.2): ronde pijlknoppen, de
// weekbalk met pijltjes, en de schakelaar (Overzicht / Weekoverzicht, Les A / Les B).

export function Chevron({ dir, size }) {
  var s = size || 20;
  var d = dir === "left" ? "M15 18l-6-6 6-6" : dir === "right" ? "M9 18l6-6-6-6" : dir === "down" ? "M6 9l6 6 6-6" : "M18 15l-6-6-6 6";
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function ArrowBtn({ dir, onClick, disabled, label, size, shadow, style }) {
  var s = size || 44;
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label}
      style={Object.assign({
        width: s, height: s, flex: "none", borderRadius: s / 2, border: shadow ? "1px solid " + T.line : "0",
        background: T.card, color: T.ink, display: "flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.3 : 1, padding: 0,
        boxShadow: shadow ? "0 2px 8px rgba(0,0,0,.12)" : "none", transition: "opacity .2s",
      }, style || {})}>
      <Chevron dir={dir} size={s >= 52 ? 24 : 20} />
    </button>
  );
}

// "‹  Week 3  ›" — de vaste weeknavigatie bovenaan, overal hetzelfde.
export function WeekNav({ week, sub, onPrev, onNext, big, badge }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontFamily: FF }}>
      <ArrowBtn dir="left" onClick={onPrev} disabled={!onPrev} label="Vorige week" />
      <div style={{ textAlign: "center", minWidth: 120 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <div style={{ fontWeight: 800, fontSize: big ? 28 : 24, color: T.ink, lineHeight: 1.15, whiteSpace: "nowrap" }}>{"Week " + week}</div>
          {badge}
        </div>
        {sub && <div style={{ fontSize: 13, color: T.muted }}>{sub}</div>}
      </div>
      <ArrowBtn dir="right" onClick={onNext} disabled={!onNext} label="Volgende week" />
    </div>
  );
}

export function Segmented({ options, value, onChange, style, height }) {
  var h = height || 38;
  return (
    <div role="tablist" style={Object.assign({ display: "flex", background: T.seg, borderRadius: 10, padding: 3, gap: 3, fontFamily: FF }, style || {})}>
      {options.map(function (o) {
        var on = o.value === value;
        return (
          <button key={o.value} type="button" role="tab" aria-selected={on} onClick={function () { onChange(o.value); }}
            style={{ flex: 1, height: h, border: 0, borderRadius: 8, background: on ? T.card : "transparent", fontSize: 14, fontWeight: on ? 700 : 500, color: on ? T.ink : "#5A564E", cursor: "pointer", boxShadow: on ? "0 1px 2px rgba(0,0,0,.06)" : "none", whiteSpace: "nowrap", padding: "0 10px" }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Kleurknop (niveau) — gevuld als hij actief is, anders wit met gekleurde rand.
export function ColorBtn({ lv, active, onClick, short, text, height, style }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      style={Object.assign({
        height: height || 44, minWidth: 0, borderRadius: 10, border: "2px solid " + lv.color,
        background: active ? lv.color : T.card, color: active ? (lv.on || "#fff") : T.ink,
        fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 6px", fontFamily: FF,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", transition: "background .2s, color .2s",
      }, style || {})}>
      {text || (short ? shortLabel(lv.label) : lv.label)}
    </button>
  );
}

// Korte naam voor de kleurknoppen: één woord, zonder seizoen ("Rood winter '26" ->
// "Rood"). Een tweede groep van dezelfde kleur ("Geel 2 ...", "2e geel ...") wordt "2e geel".
var SEASON = /\b(winter|zomer|voorjaar|najaar|herfst|lente|seizoen)\b.*$|['’]\d{2}\b.*$|\b20\d{2}\b.*$/i;
export function shortLabel(s) {
  if (!s) return "";
  var base = s.replace(SEASON, "").replace(/[()\-–,]+/g, " ").trim();
  var words = base.split(/\s+/).filter(Boolean);
  if (words.length === 0) return s.split(/\s+/)[0];
  var nr = words.find(function (x) { return /^(2e?|ii|tweede)$/i.test(x); });
  if (nr) {
    var col = words.find(function (x) { return x !== nr; });
    if (col) return "2e " + col.toLowerCase();
  }
  return words[0];
}

// Klein label naast het weeknummer ("deze week", "volgende les") of een knop terug
// naar de huidige week.
export function WeekBadge({ kind, onClick }) {
  var base = { fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 10, whiteSpace: "nowrap", fontFamily: FF, lineHeight: 1.3 };
  if (kind === "now") return <span style={Object.assign({}, base, { background: T.ink, color: "#fff" })}>deze week</span>;
  if (kind === "next") return <span style={Object.assign({}, base, { background: "#fff", color: T.ink, border: "1px solid #D9D4C9" })}>volgende les</span>;
  if (kind === "back") return (
    <button type="button" onClick={onClick} style={Object.assign({}, base, { background: "#fff", color: T.ink, border: "1px solid #D9D4C9", cursor: "pointer", minHeight: 24 })}>Deze week</button>
  );
  return null;
}

// ---------------------------------------------------------------- build 2.5

// Filterchip (tags, bronnen). Actief = gekleurde rand + lichte vulling.
export function Chip({ active, onClick, children, color, bg, style, noX }) {
  var c = color || T.primary;
  return (
    <button type="button" aria-pressed={!!active} onClick={onClick}
      style={Object.assign({
        minHeight: 34, padding: "0 12px", borderRadius: 999, fontFamily: FF, fontSize: 13, cursor: "pointer",
        border: "1px solid " + (active ? c : T.line), background: active ? (bg || "#E9EDF5") : T.soft,
        color: active ? c : "#4A4740", fontWeight: active ? 700 : 600, whiteSpace: "nowrap",
        display: "inline-flex", alignItems: "center", gap: 6, flex: "none",
      }, style || {})}>
      {children}
      {active && !noX && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>}
    </button>
  );
}

// Aan/uit-schakelaar.
export function Toggle({ on, onClick, label, color }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} onClick={onClick}
      style={{ width: 48, height: 28, borderRadius: 14, border: 0, background: on ? (color || "#2F7D47") : "#CFC9BD", position: "relative", padding: 0, flex: "none", cursor: "pointer", transition: "background .15s" }}>
      <span style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", transition: "left .15s", boxShadow: "0 1px 2px rgba(0,0,0,.2)" }} />
    </button>
  );
}

// Klein kopje boven een blok ("KLEUR", "TAGS").
export function Label({ children, style, htmlFor }) {
  var s = Object.assign({ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: T.muted, marginBottom: 6, fontFamily: FF }, style || {});
  return htmlFor ? <label htmlFor={htmlFor} style={s}>{children}</label> : <span style={s}>{children}</span>;
}

// Tag-label op een kaart.
export function Tag({ text, lv }) {
  var l = lv || { bg: "#F0F1F3", ink: "#3D4550", border: "#C6CAD1" };
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: l.bg, color: l.ink || l.color, border: "1px solid " + l.border, whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "100%", boxSizing: "border-box", display: "inline-block", lineHeight: 1.35 }}>{text}</span>;
}

// Rond icoon met initialen + naam ("Gedeeld door ...").
export function Author({ name, prefix, size }) {
  var parts = String(name || "?").trim().split(/\s+/);
  var ini = (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : "")).toUpperCase();
  var s = size || 20;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#4A4740", fontWeight: 600, fontFamily: FF, minWidth: 0 }}>
      <span aria-hidden="true" style={{ width: s, height: s, borderRadius: "50%", background: T.seg, color: T.primary, fontSize: s <= 20 ? 9 : 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{ini}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(prefix || "") + (name || "Onbekend")}</span>
    </span>
  );
}

// Kleine icoontjes (stroke) voor de bibliotheek.
var LI = {
  folder: ["M3 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"],
  court: ["M4 3h16v18H4z", "M4 12h16", "M12 3v18"],
  plus: ["M12 5v14", "M5 12h14"],
  copy: ["M8 8h12v12H8z", "M16 8V4H4v12h4"],
  clock: ["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M12 7v5l3 2"],
  share: ["M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z", "M2.5 20c.8-3.5 3.4-5.5 6.5-5.5s5.7 2 6.5 5.5", "M16 4.5a3.5 3.5 0 0 1 0 7", "M18 14.8c1.9.7 3.1 2.5 3.5 5.2"],
  lock: ["M6 11h12v10H6z", "M8 11V7a4 4 0 0 1 8 0v4"],
  image: ["M4 5h16v14H4z", "M4 16l5-5 4 4 3-3 4 4", "M15.5 9.5h.01"],
};
export function LibIcon({ name, size, style }) {
  return (
    <svg width={size || 18} height={size || 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={Object.assign({ flex: "none" }, style || {})}>
      {(LI[name] || []).map(function (d, i) { return <path key={i} d={d} />; })}
    </svg>
  );
}

// Primaire knop (donkerblauw) en secundaire knop (wit met rand).
export function Btn({ onClick, children, kind, disabled, style, type, title }) {
  var k = kind || "primary";
  var base = {
    minHeight: 44, padding: "0 16px", borderRadius: 10, fontFamily: FF, fontSize: 14, fontWeight: 700,
    cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
  };
  var look = k === "primary" ? { background: T.primary, color: "#fff", border: 0 }
    : k === "danger" ? { background: "#fff", color: "#B83232", border: "1px solid " + T.line }
    : { background: "#fff", color: T.primary, border: "1px solid " + T.line };
  return <button type={type || "button"} title={title} onClick={onClick} disabled={disabled} style={Object.assign(base, look, style || {})}>{children}</button>;
}

// Favoriet-sterretje (build 2.6): leeg = geen favoriet, geel gevuld = favoriet.
// Staat los van de kaart (eigen knop), zodat tikken de kaart niet opent.
export function StarBtn({ on, onClick, size, label, style }) {
  var sz = size || 22;
  return (
    <button type="button" aria-pressed={!!on} aria-label={(on ? "Verwijderen uit favorieten" : "Toevoegen aan favorieten") + (label ? ": " + label : "")}
      title={on ? "Favoriet" : "Favoriet maken"}
      onClick={function (e) { e.stopPropagation(); e.preventDefault(); onClick(); }}
      style={Object.assign({ width: 44, height: 44, border: 0, background: "none", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flex: "none" }, style || {})}>
      <svg width={sz} height={sz} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3.2l2.7 5.6 6.1.8-4.5 4.2 1.1 6-5.4-2.9-5.4 2.9 1.1-6-4.5-4.2 6.1-.8z" fill={on ? "#E8C12E" : "none"} stroke={on ? "#C9A20F" : "#A39E94"} strokeWidth={on ? 1.4 : 1.7} strokeLinejoin="round" />
      </svg>
    </button>
  );
}

// Filterchip "★ Favorieten".
export function FavChip({ active, onClick, short }) {
  return (
    <button type="button" aria-pressed={!!active} onClick={onClick}
      style={{ minHeight: 34, padding: "0 12px", borderRadius: 999, fontFamily: FF, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
        border: "1px solid " + (active ? "#C9A20F" : T.line), background: active ? "#FFF6D6" : "#fff", color: active ? "#7A5C00" : "#4A4740",
        fontWeight: active ? 800 : 700, display: "inline-flex", alignItems: "center", gap: 5, flex: "none" }}>
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.2l2.7 5.6 6.1.8-4.5 4.2 1.1 6-5.4-2.9-5.4 2.9 1.1-6-4.5-4.2 6.1-.8z" fill={active ? "#E8C12E" : "none"} stroke={active ? "#C9A20F" : "#8A857B"} strokeWidth="2" strokeLinejoin="round" /></svg>
      {short ? "Favorieten" : "Favorieten"}
    </button>
  );
}
